import { useScrollParallax } from "@/hooks/ui/useScrollParallax";
import { useSectionHeights } from "@/hooks/ui/useSectionHeights";
import {
  HeroHeader,
  IntegrationCarousel,
  WhyChooseSection,
  BentoGrid,
  FinalCTA,
  ChaosToTidySection,
  StartEarlySection
} from "./sections";
import { SetupSection } from "./SetupSection";

const RedesignedHero = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  // Parallax transition between carousel and bento sections
  const { carouselRef, bentoRef, carouselStyle, bentoStyle, prefersReducedMotion } = useScrollParallax();

  // Dynamic section height measurement for background colors
  const {
    containerRef: bentoContainerRef,
    manageStartRef,
    growStartRef,
    businessStartRef,
    businessEndRef,
    managePercent,
    growPercent,
    businessPercent,
  } = useSectionHeights();



  return (
    <div className="relative bg-background text-foreground">
      {/* Hero Section */}
      <div className="relative z-10 bg-sidebar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 min-h-main-content flex flex-col justify-between">
          <HeroHeader scrollToSection={scrollToSection} />
          <IntegrationCarousel />
        </div>

        {/* Parallax Transition Container - unified background behind both sections */}
        <div className="relative">
          {/* Why Choose Tag-Inn Section - Modern with Horizontal Scroll */}
          {/* Wrapped in parallax exit container */}
          <div
            ref={carouselRef}
            className="parallax-exit relative z-10 bg-background"
            style={!prefersReducedMotion ? carouselStyle : undefined}
          >
            <WhyChooseSection />
          </div>

          {/* Bento section backgrounds - NOT transformed, positioned behind content */}
          {/* These use CSS grid-like positioning to match section heights */}
          <div className="absolute left-0 right-0 pointer-events-none" style={{ top: '0', bottom: '0' }}>
            {/* Background for carousel area */}
            <div className="bg-background" style={{ height: 'auto' }} />
          </div>

          {/* Enhanced Bento Box Grid - MANAGE, GROW, BUSINESS */}
          <div className="pt-24 sm:pt-32 pb-16 relative z-10 overflow-hidden">
            {/* Section background colors - NOT affected by parallax */}
            <div
              className="absolute inset-0 pointer-events-none -z-10"
              style={{
                background: `linear-gradient(to bottom,
                  hsl(var(--background)) 0%,
                  rgba(var(--primary-rgb), 0.12) ${managePercent * 0.15}%,
                  rgba(var(--primary-rgb), 0.12) ${managePercent * 0.7}%,
                  rgba(165, 94, 78, 0.12) ${managePercent}%,
                  rgba(var(--grow-rgb), 0.12) ${managePercent + growPercent * 0.3}%,
                  rgba(var(--grow-rgb), 0.12) ${managePercent + growPercent * 0.7}%,
                  rgba(139, 196, 130, 0.12) ${managePercent + growPercent}%,
                  rgba(var(--secondary-rgb), 0.12) ${managePercent + growPercent + businessPercent * 0.3}%,
                  rgba(var(--secondary-rgb), 0.12) ${managePercent + growPercent + businessPercent * 0.85}%,
                  hsl(var(--background)) 100%
                )`
              }}
            />
            {/* Only the content gets parallax effects */}
            <div
              ref={bentoRef}
              className="parallax-enter"
              style={!prefersReducedMotion ? bentoStyle : undefined}
            >
              <BentoGrid
                containerRef={bentoContainerRef}
                manageStartRef={manageStartRef}
                growStartRef={growStartRef}
                businessStartRef={businessStartRef}
                businessEndRef={businessEndRef}
              />
            </div>
          </div>

          {/* Setup Section - Positioned between Bento and CTA */}
          <div className="relative z-10">
            <SetupSection />
          </div>

          {/* Chaos to Tidy Section - Shows app convergence */}
          <div className="relative z-10">
            <ChaosToTidySection />
          </div>

          {/* Start Early Section - For businesses not open yet */}
          <div className="relative z-10">
            <StartEarlySection />
          </div>
        </div>

        {/* Final CTA Section - Bold & Vibrant Convergence */}
        <FinalCTA />
      </div>
    </div>
  );
};

export default RedesignedHero;
